import type { JSX } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { GUIDE_CONTENTS_PAGE, GUIDE_TITLE, GUIDE_MISSING } from '../game/copy.ts';
import { parseMarkdown, type Block, type Doc, type Inline } from '../game/markdown.ts';
import { useShell } from '../idle/shell.ts';
import { Button } from '../ui/Button.tsx';
import { Sheet } from '../ui/Sheet.tsx';

/**
 * The player guide, inside the game (D79). `docs/player/*.md` and its screenshots are
 * bundled at build time by the two globs below, so the guide is the one that shipped with
 * this build, works offline, and cannot drift from the release. It is drawn in the app's
 * own type and colours by `game/markdown.ts` plus the renderers here; no dependency.
 *
 * It opens from Settings and from anywhere else that calls `openGuide(page)` with a page
 * name, so a hint can point at the page that answers it.
 */

/** The pages, keyed by file name: `first-hour.md`. */
const PAGES: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob('../../../../docs/player/*.md', {
      query: '?raw',
      import: 'default',
      eager: true,
    }) as Record<string, string>,
  ).map(([path, text]) => [path.split('/').pop() ?? path, text]),
);

/** The screenshots, keyed the way the Markdown writes them: `images/world-fresh.png`. */
const IMAGES: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob('../../../../docs/player/images/*.png', {
      query: '?url',
      import: 'default',
      eager: true,
    }) as Record<string, string>,
  ).map(([path, url]) => [`images/${path.split('/').pop() ?? path}`, url]),
);

/** Parsed once per page per session: the pages are constants of the build. */
const PARSED = new Map<string, Doc>();
function docFor(page: string): Doc | null {
  const src = PAGES[page];
  if (src === undefined) return null;
  const had = PARSED.get(page);
  if (had) return had;
  const doc = parseMarkdown(src);
  PARSED.set(page, doc);
  return doc;
}

export interface GuideEntry {
  page: string;
  label: string;
}

/**
 * The contents, read from the README's own list, so the guide's order lives in the guide
 * rather than in a second list here. The README itself heads it.
 */
export function guideContents(): GuideEntry[] {
  const doc = docFor(GUIDE_CONTENTS_PAGE);
  const out: GuideEntry[] = [{ page: GUIDE_CONTENTS_PAGE, label: 'Contents' }];
  for (const block of doc?.blocks ?? []) {
    if (block.type !== 'list') continue;
    for (const item of block.items) {
      const first = item[0];
      if (first?.type === 'page' && !out.some((e) => e.page === first.page)) {
        out.push({ page: first.page, label: first.text });
      }
    }
  }
  return out;
}

const LINK: JSX.CSSProperties = {
  color: 'var(--accent)',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  cursor: 'pointer',
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
};

function Inlines({ nodes, onPage }: { nodes: Inline[]; onPage: (page: string) => void }) {
  return (
    <>
      {nodes.map((n, i) =>
        n.type === 'strong' ? (
          <b key={i} style={{ fontWeight: 600 }}>
            {n.text}
          </b>
        ) : n.type === 'page' ? (
          <button key={i} type="button" style={LINK} onClick={() => onPage(n.page)}>
            {n.text}
          </button>
        ) : n.type === 'external' ? (
          <a key={i} href={n.href} target="_blank" rel="noreferrer noopener" style={LINK}>
            {n.text}
          </a>
        ) : n.type === 'image' ? (
          <img key={i} src={IMAGES[n.src] ?? n.src} alt={n.alt} style={{ maxWidth: '100%' }} />
        ) : (
          <span key={i}>{n.text}</span>
        ),
      )}
    </>
  );
}

const HEADING: Record<1 | 2 | 3, JSX.CSSProperties> = {
  1: { font: '600 20px/1.3 var(--font-ui)', margin: '4px 0 0' },
  2: { font: '600 16px/1.3 var(--font-ui)', margin: '12px 0 0' },
  3: { font: '600 14px/1.3 var(--font-ui)', margin: '8px 0 0', color: 'var(--ink-2)' },
};

/**
 * The sheet's panel: header, page picker, then the page itself in the space that is left,
 * so the way out and the picker stay put while a long page scrolls under them.
 */
const PANEL: JSX.CSSProperties = {
  height: '100%',
  maxHeight: '100%',
  gap: 10,
  overflow: 'hidden',
  gridTemplateRows: 'auto auto 1fr',
  alignContent: 'stretch',
};

const CELL: JSX.CSSProperties = {
  textAlign: 'left',
  verticalAlign: 'top',
  padding: '6px 10px',
  borderBottom: '1px solid var(--line)',
};

function Blocks({ blocks, onPage }: { blocks: Block[]; onPage: (page: string) => void }) {
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'heading') {
          const H = (b.level === 1 ? 'h1' : b.level === 2 ? 'h2' : 'h3') as 'h1';
          return (
            <H key={i} style={HEADING[b.level]}>
              <Inlines nodes={b.inline} onPage={onPage} />
            </H>
          );
        }
        if (b.type === 'paragraph') {
          return (
            <p key={i} style={{ margin: 0, font: '400 14px/1.55 var(--font-ui)' }}>
              <Inlines nodes={b.inline} onPage={onPage} />
            </p>
          );
        }
        if (b.type === 'list') {
          return (
            <ul
              key={i}
              style={{
                margin: 0,
                paddingLeft: 20,
                display: 'grid',
                gap: 6,
                font: '400 14px/1.55 var(--font-ui)',
              }}
            >
              {b.items.map((item, j) => (
                <li key={j}>
                  <Inlines nodes={item} onPage={onPage} />
                </li>
              ))}
            </ul>
          );
        }
        if (b.type === 'figure') {
          return (
            <figure key={i} style={{ margin: 0, display: 'grid', gap: 6 }}>
              <img
                src={IMAGES[b.src] ?? b.src}
                alt={b.alt}
                loading="lazy"
                style={{
                  width: '100%',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--surface)',
                }}
              />
              <figcaption style={{ font: '400 12px/1.4 var(--font-ui)', color: 'var(--ink-2)' }}>
                {b.alt}
              </figcaption>
            </figure>
          );
        }
        return (
          <div
            key={i}
            style={{
              overflowX: 'auto',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <table
              style={{
                borderCollapse: 'collapse',
                width: '100%',
                font: '400 13px/1.4 var(--font-ui)',
              }}
            >
              <thead>
                <tr>
                  {b.head.map((cell, j) => (
                    <th key={j} style={{ ...CELL, color: 'var(--ink-2)', fontWeight: 600 }}>
                      <Inlines nodes={cell} onPage={onPage} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((row, j) => (
                  <tr key={j}>
                    {row.map((cell, k) => (
                      <td key={k} style={CELL}>
                        <Inlines nodes={cell} onPage={onPage} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}

/** The guide sheet. Full screen, because a page of prose is not an action sheet. */
export function GuideScreen() {
  const g = useShell();
  const page = g.guidePage;
  const contents = useMemo(guideContents, []);
  /** Where Back goes, most recent last. Emptied when the guide closes. */
  const [trail, setTrail] = useState<string[]>([]);
  const body = useRef<HTMLDivElement | null>(null);
  const open = page !== null;

  useEffect(() => {
    if (!open) setTrail([]);
    // A new page starts at its own top, not where the last one was read to.
    else if (body.current) body.current.scrollTop = 0;
  }, [open, page]);

  if (!open || page === null) return null;
  const doc = docFor(page);
  const go = (next: string) => {
    if (next === page) return;
    setTrail((t) => [...t, page]);
    g.openGuide(next);
  };
  const back = () => {
    const to = trail[trail.length - 1];
    setTrail((t) => t.slice(0, -1));
    g.openGuide(to ?? GUIDE_CONTENTS_PAGE);
  };
  const canBack = trail.length > 0 || page !== GUIDE_CONTENTS_PAGE;

  return (
    <Sheet
      title={doc?.title ?? GUIDE_TITLE}
      ariaLabel={GUIDE_TITLE}
      screenLabel="Guide"
      testId="guide"
      onClose={g.closeGuide}
      // Over the idle settings sheet it opens from (40), which used to cover it.
      zIndex={50}
      maxWidth={720}
      style={PANEL}
    >
      <div
        style={{
          display: 'flex',
          gap: 6,
          overflowX: 'auto',
          paddingBottom: 2,
          borderBottom: '1px solid var(--line)',
        }}
      >
        {canBack && (
          <Button size="sm" variant="ghost" onClick={back}>
            Back
          </Button>
        )}
        {contents.map((e) => (
          <Button
            key={e.page}
            size="sm"
            variant={e.page === page ? 'primary' : 'secondary'}
            onClick={() => go(e.page)}
          >
            {e.label}
          </Button>
        ))}
      </div>
      <div
        ref={body}
        data-testid="guide-body"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'grid',
          gap: 10,
          alignContent: 'start',
        }}
      >
        {doc ? (
          <Blocks blocks={doc.blocks} onPage={go} />
        ) : (
          <p style={{ margin: 0, font: '400 14px/1.55 var(--font-ui)', color: 'var(--ink-2)' }}>
            {GUIDE_MISSING}
          </p>
        )}
      </div>
    </Sheet>
  );
}
